import type { AppRouter } from '@nemo/api';
import { httpBatchLink, httpLink, splitLink, type TRPCLink } from '@trpc/client';

/** The fetch a link takes, which @trpc/client does not export by name. */
type LinkFetch = NonNullable<Parameters<typeof httpLink>[0]['fetch']>;

/**
 * Past this many characters of input a query goes as a POST: Node refuses a
 * request whose headers pass 16 KB (431), and a URL is a header. Hundreds of
 * ticked ids are ~22 KB.
 */
const LONG_INPUT_CHARS = 4_000;
/** Splits a batch of small queries before its URL nears the same limit. */
const MAX_URL_CHARS = 8_000;

/**
 * What the page says when the server's answer is not tRPC's JSON — a proxy's
 * or Node's own refusal, an empty body — instead of the parser's "Unexpected
 * end of JSON input".
 */
export function unreadableAnswerMessage(status: number): string {
  switch (status) {
    case 413:
    case 414:
    case 431:
      return 'That was too much to send in one go. Select fewer and try again.';
    case 401:
    case 403:
      return 'You have been signed out. Reload the page to sign in again.';
    case 502:
    case 503:
    case 504:
      return 'The server is busy or restarting. Try again in a minute.';
    default:
      return `The server could not answer that (HTTP ${status}). Try again in a minute.`;
  }
}

const readableFetch: LinkFetch = async (input, init) => {
  let response: Response;
  try {
    response = await globalThis.fetch(
      input,
      init === undefined ? undefined : { ...init, signal: init.signal ?? null },
    );
  } catch (error) {
    if (init?.signal?.aborted === true) throw error;
    throw new Error('Could not reach the server. Check the connection and try again.', {
      cause: error,
    });
  }
  // Every answer tRPC writes, errors included, is JSON.
  if ((response.headers.get('content-type') ?? '').includes('application/json')) return response;
  throw new Error(unreadableAnswerMessage(response.status));
};

function inputLength(input: unknown): number {
  return input === undefined ? 0 : (JSON.stringify(input)?.length ?? 0);
}

/**
 * How the investors app talks to its api: small queries batched as GETs, a
 * large one alone as a POST (the api allows the override), and an answer
 * that is not JSON turned into words a person can act on.
 */
export function trpcLinks(): TRPCLink<AppRouter>[] {
  // Vite proxies /trpc to the api in development; in production the api
  // serves the bundle itself, so the same relative URL works either way.
  const url = '/trpc';
  return [
    splitLink({
      condition: (op) => op.type === 'query' && inputLength(op.input) > LONG_INPUT_CHARS,
      true: httpLink({ url, methodOverride: 'POST', fetch: readableFetch }),
      false: httpBatchLink({ url, maxURLLength: MAX_URL_CHARS, fetch: readableFetch }),
    }),
  ];
}
